/**
 * Empty-search panel — Cornerstone integration.
 *
 * Shows sections/empty-search.liquid (#empty-search-container) under the
 * header whenever the theme's native quick-search is open with an EMPTY
 * query. Handles trending tags (Liquid), search history (localStorage) and a
 * scrollable popular-products carousel.
 *
 * Positioning: this panel is reparented (moved, not cloned) into the SAME
 * <form data-quick-search-form> element that the theme's own native
 * predictive-search results (.quick-search__results-wrapper) already live
 * in, as an extra sibling right alongside them - see showBox() below. That
 * form is what the theme keeps correctly positioned under the search bar in
 * every situation (desktop dropdown, mobile bottom-sheet layout, sticky
 * header, breakpoint switches, window resize - see assets/theme.js's
 * _setLayoutVars()/quick-search CSS). Earlier versions of this file
 * re-implemented that positioning independently in JS (measuring the bar's
 * rect, tracking scroll offset, detecting sticky-header mode, re-measuring
 * on resize/scroll, animating the open transition...) and kept finding new
 * edge cases that logic didn't cover. Becoming an actual child of the same
 * form the theme already positions correctly sidesteps all of that: this
 * panel now inherits exactly the same behavior as the native dropdown, with
 * no positioning code of our own left to get subtly wrong.
 *
 * Cornerstone quick-search hooks used:
 *   [data-quick-search]              -> data-quick-search-active="true" when open
 *   [data-quick-search-input]        -> the search text input
 *   [data-quick-search-form]         -> the form the native results live in (and
 *                                        this panel is moved into) - also where
 *                                        the submit event is read for history
 *
 * The pure logic (history + escaping + history-HTML) is exported via
 * module.exports when loaded under Node (for unit tests); the DOM wiring only
 * auto-runs in a browser.
 */
(function () {
  "use strict";

  var HISTORY_KEY = "rbg_search_history";
  var MAX_HISTORY = 8;

  // Assigned in init() (browser only) so the module can be required under Node.
  var box = null;

  // ---- search history (localStorage) --------------------------------------
  function getHistory() {
    try {
      var raw = localStorage.getItem(HISTORY_KEY);
      var arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr : [];
    } catch (e) {
      return [];
    }
  }

  function saveTerm(term) {
    term = (term || "").trim();
    if (!term) return;
    var lower = term.toLowerCase();
    var list = getHistory().filter(function (t) {
      return t.toLowerCase() !== lower;
    });
    list.unshift(term);
    list = list.slice(0, MAX_HISTORY);
    try {
      localStorage.setItem(HISTORY_KEY, JSON.stringify(list));
    } catch (e) {
      /* storage full / disabled — ignore */
    }
  }

  function escapeHtml(s) {
    return String(s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  // Pure: builds the history markup for a given list of terms.
  function renderHistoryHTML(list) {
    if (!list || !list.length) {
      return '<p class="empty-search-empty-note">No recent searches</p>';
    }
    return list
      .map(function (term) {
        return (
          '<a class="empty-search-history-tag" href="/search?q=' +
          encodeURIComponent(term) +
          '">' +
          escapeHtml(term) +
          "</a>"
        );
      })
      .join("");
  }

  function renderHistory() {
    var container = box.querySelector("[data-empty-search-history]");
    if (!container) return;
    container.innerHTML = renderHistoryHTML(getHistory());
  }

  // ---- show / hide ---------------------------------------------------------
  // Moves the panel into the currently-open quick-search instance's own
  // <form data-quick-search-form> - the same element the native
  // .quick-search__results-wrapper lives in - so it's positioned by the
  // theme's own (already correct) CSS/JS instead of anything of ours. This is
  // idempotent: if the panel's already in there (or being reopened on the
  // same instance), appendChild just no-ops/reorders it as the last child.
  function attachTo(quickSearch) {
    var form = quickSearch.querySelector("[data-quick-search-form]");
    if (form && box.parentNode !== form) {
      form.appendChild(box);
    }
  }

  function showBox(quickSearch) {
    var anchor = quickSearch || document.querySelector("[data-quick-search]");
    if (!anchor) return;
    attachTo(anchor);
    renderHistory();
    box.classList.add("is-visible");
    box.setAttribute("aria-hidden", "false");
  }

  function hideBox() {
    box.classList.remove("is-visible");
    box.setAttribute("aria-hidden", "true");
  }

  // A quick-search is in the "empty" state when it is open and its input is blank.
  function evaluate(quickSearch) {
    var open = quickSearch.getAttribute("data-quick-search-active") === "true";
    var input = quickSearch.querySelector("[data-quick-search-input]");
    var hasQuery = input && input.value.trim() !== "";
    if (open && !hasQuery) {
      showBox(quickSearch);
    } else {
      hideBox();
    }
  }

  // ---- carousel arrows -----------------------------------------------------
  function initCarousel() {
    var scroller = box.querySelector("[data-empty-search-scroll]");
    var prev = box.querySelector("[data-empty-search-prev]");
    var next = box.querySelector("[data-empty-search-next]");
    if (!scroller) return;

    function step() {
      // scroll by roughly one card + gap
      var first = scroller.querySelector(".empty-search-product");
      return first ? first.getBoundingClientRect().width + 16 : 240;
    }
    function updateArrows() {
      if (!prev || !next) return;
      var maxScroll = scroller.scrollWidth - scroller.clientWidth - 1;
      prev.hidden = scroller.scrollLeft <= 0;
      next.hidden = scroller.scrollLeft >= maxScroll;
    }
    if (prev) {
      prev.addEventListener("click", function () {
        scroller.scrollLeft -= step() * 2;
      });
    }
    if (next) {
      next.addEventListener("click", function () {
        scroller.scrollLeft += step() * 2;
      });
    }
    scroller.addEventListener("scroll", updateArrows, { passive: true });
    updateArrows();
    // Re-check once shown (widths are 0 while hidden).
    box._updateArrows = updateArrows;
  }

  // ---- popular-products cards: quick-view + add-to-cart --------------------
  // sections/empty-search.liquid renders full snippets/product-item.liquid
  // cards here (quick-view button + quick-add button/quantity-input markup
  // included), but Cornerstone only wires those buttons up via its own
  // ProductItem(container) component (assets/theme.js), which only ever runs
  // on containers belonging to a registered section. This panel isn't a
  // registered section, so ProductItem() never ran here and the buttons had
  // no click listeners at all - not a z-index/overlay issue, they were just
  // inert markup. ProductItem isn't exposed globally, so its two behaviors
  // are replicated here using the same public hooks the rest of the theme
  // already relies on for this exact purpose:
  //   - quick-view      -> window.emit("quick-view:open", ...): the same bus
  //     event product-item's own button dispatches (see custom-events.js's
  //     cart-rec-quick-view bridge for the established precedent).
  //   - quick-add (has variants) -> window.emit("quick-add:open", ...): opens
  //     the same quick-add modal used everywhere else.
  //   - direct add (single variant) -> POST /cart/add.js, then emit
  //     "cart:updated" / "quick-cart:updated", the same two events
  //     assets/theme.js's own cart.addItemById() emits after adding, so the
  //     header cart count and any open cart drawer refresh exactly as they
  //     would from a normal product-item add.
  function addToCart(variantId, wrapper) {
    if (typeof window.emit === "function") window.emit("cart:updating");
    fetch("/cart/add.js", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
    })
      .then(function () {
        return fetch("/cart.js", { credentials: "include" }).then(function (res) {
          return res.json();
        });
      })
      .then(function (cart) {
        if (wrapper) syncWrapperQuantity(wrapper, 1);
        if (typeof window.emit !== "function") return;
        window.emit("quick-cart:updated");
        window.emit("cart:updated", { cart: cart });
      })
      .catch(function () {
        /* fire-and-forget, matches the theme's own add flow */
      });
    if (wrapper) wrapper.classList.add("active");
  }

  // Updates the quantity that's already in the cart for a single-variant line
  // (the "+"/"-"/remove controls that quick-add-quantity-atc.liquid renders
  // once an item has quantity >= 1 - see snippets/quantity-input.liquid for the
  // [data-add-quantity]/[data-subtract-quantity]/[data-remove-item] markup).
  // Same rationale as addToCart() above: Cornerstone's own handler for these
  // (initQuickAddQuantitySelectors, inside ProductItem()) never ran here, so
  // they're plain inert buttons without this.
  function changeCartLine(variantId, newQuantity, wrapper) {
    newQuantity = Math.max(0, newQuantity);
    if (typeof window.emit === "function") window.emit("cart:updating");
    fetch("/cart/change.js", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: variantId, quantity: newQuantity }),
    })
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        syncWrapperQuantity(wrapper, newQuantity);
        if (typeof window.emit !== "function") return;
        window.emit("quick-cart:updated");
        window.emit("cart:updated", { cart: cart });
      })
      .catch(function () {
        /* fire-and-forget, matches addToCart()'s error handling */
      });
  }

  // Keeps the card's own UI in sync after an add/change/remove, since this
  // panel's markup is rendered once server-side and never re-fetched - unlike
  // a normal collection grid, nothing else will update these elements for us.
  //
  // theme.css's minus-vs-trash-icon toggle (the [data-quantity-in-cart="0"]
  // rules) matches on ANY ancestor carrying a data-quantity-in-cart attribute
  // - and snippets/product-item.liquid puts that attribute on BOTH the outer
  // .product-item AND the quick-add wrapper as two separate copies. Updating
  // only the wrapper's copy left the outer .product-item's stuck at its
  // stale server-rendered "0", which still matched the "hide subtract/minus"
  // rule even once the wrapper's own copy correctly showed a higher count -
  // hiding both the minus AND the remove/trash icon at once (mirrors
  // theme.js's own _handleUpdateProductCount, which updates the item itself
  // plus every [data-quantity-in-cart] descendant for exactly this reason).
  function syncWrapperQuantity(wrapper, quantity) {
    var productItem = wrapper.closest(".product-item");
    var scope = productItem || wrapper;
    scope.dataset.quantityInCart = String(quantity);
    var withAttr = scope.querySelectorAll("[data-quantity-in-cart]");
    for (var i = 0; i < withAttr.length; i++) {
      withAttr[i].dataset.quantityInCart = String(quantity);
    }
    var input = wrapper.querySelector("[data-product-quantity-input]");
    if (input) input.value = quantity;
    if (quantity <= 0) wrapper.classList.remove("active");
  }

  function initPopularProducts() {
    var scroller = box.querySelector("[data-empty-search-scroll]");
    if (!scroller) return;

    scroller.addEventListener("click", function (e) {
      var quickViewBtn = e.target.closest(".show-product-quick-view");
      if (quickViewBtn) {
        e.preventDefault();
        var productEl = quickViewBtn.closest(".product-item");
        if (!productEl || typeof window.emit !== "function") return;
        window.emit("quick-view:open", null, {
          type: "quick-view",
          productUrl: productEl.dataset.url,
          productMedia: productEl.querySelector(".product-item__image-link"),
          productBadges: productEl.querySelector(".product-badges"),
          productTitle: productEl.dataset.title,
        });
        return;
      }

      // Quantity stepper (+/-/remove) - only present once quantity >= 1 (see
      // snippets/quantity-input.liquid). Matches the theme's own behavior:
      // the trash/remove icon shows at quantity 1 (theme.css's
      // [data-quantity-in-cart="0"|"1"] rules), "-" takes over once quantity
      // is 2+, letting you step back down to 1 before removing entirely. The
      // variant id lives on the sibling "Add to Cart" button
      // (data-variant-id), which stays in the DOM even while CSS hides it in
      // favour of the stepper, so it's still reachable.
      var stepperBtn = e.target.closest(
        "[data-add-quantity], [data-subtract-quantity], [data-remove-item]"
      );
      if (stepperBtn) {
        var stepperWrapper = stepperBtn.closest(".product-item__quick-add-wrapper");
        if (!stepperWrapper) return;
        var stepperAddBtn = stepperWrapper.querySelector(".product-item__quick-add");
        var stepperVariantId = stepperAddBtn && stepperAddBtn.dataset.variantId;
        if (!stepperVariantId) return;

        var currentQty = parseInt(stepperWrapper.dataset.quantityInCart, 10) || 0;
        var nextQty = currentQty;
        if (stepperBtn.hasAttribute("data-add-quantity")) {
          nextQty = currentQty + 1;
        } else if (stepperBtn.hasAttribute("data-subtract-quantity")) {
          nextQty = currentQty - 1;
        } else if (stepperBtn.hasAttribute("data-remove-item")) {
          nextQty = 0;
        }
        changeCartLine(stepperVariantId, nextQty, stepperWrapper);
        return;
      }

      var addBtn = e.target.closest(".product-item__quick-add");
      if (!addBtn) return;

      var wrapper = addBtn.closest(".product-item__quick-add-wrapper");
      var productItem = addBtn.closest(".product-item");
      if (!wrapper || !productItem) return;

      if (wrapper.dataset.hasVariants === "true") {
        if (typeof window.emit !== "function") return;
        window.emit("quick-add:open", null, {
          type: "quick-add",
          productUrl: productItem.dataset.url,
          productTitle: productItem.dataset.title,
        });
        return;
      }

      var quantityInCart = parseInt(addBtn.dataset.quantityInCart, 10) || 0;
      var variantId = addBtn.dataset.variantId;
      if (quantityInCart < 1 && variantId) {
        addToCart(variantId, wrapper);
      } else {
        wrapper.classList.add("active");
      }
    });
  }

  // ---- wire up each quick-search instance (desktop + mobile) ---------------
  function init() {
    box = document.getElementById("empty-search-container");
    if (!box) return;

    // Cornerstone closes the search on any click outside [data-quick-search]
    // (a window click listener). Even though this panel is now reparented
    // inside [data-quick-search] itself (see attachTo()), stop clicks inside
    // it from bubbling anyway — a defensive no-op most of the time, but cheap
    // insurance against that outside-click check firing on some intermediate
    // bubble path. Links still navigate, since this stops propagation, not
    // the default action.
    box.addEventListener("click", function (e) {
      e.stopPropagation();
    });

    initCarousel();
    initPopularProducts();

    var quickSearches = document.querySelectorAll("[data-quick-search]");
    quickSearches.forEach(function (quickSearch) {
      // React to open/close (the theme toggles this attribute).
      new MutationObserver(function () {
        evaluate(quickSearch);
        if (box.classList.contains("is-visible") && box._updateArrows) {
          box._updateArrows();
        }
      }).observe(quickSearch, {
        attributes: true,
        attributeFilter: ["data-quick-search-active"],
      });

      // React to typing.
      var input = quickSearch.querySelector("[data-quick-search-input]");
      if (input) {
        input.addEventListener("input", function () {
          evaluate(quickSearch);
        });
      }

      // React to the theme's own "Clear" button (assets/theme.js _handleReset)
      // separately: it clears the field with a direct `inputEl.value = ""`
      // assignment rather than a real form reset or keystroke, and setting
      // .value programmatically never fires an "input" event - so the listener
      // above never sees it and this panel stayed hidden. Deferred to the next
      // tick so we read the input value AFTER the theme's own (synchronous)
      // reset has actually cleared it, regardless of listener registration order.
      var clearButton = quickSearch.querySelector("[data-quick-search-clear]");
      if (clearButton) {
        clearButton.addEventListener("click", function () {
          setTimeout(function () {
            evaluate(quickSearch);
          }, 0);
        });
      }

      // Save the term to history when the search is submitted.
      var form = quickSearch.querySelector("[data-quick-search-form]");
      if (form) {
        form.addEventListener("submit", function () {
          if (input) saveTerm(input.value);
        });
      }
    });

    // No resize/scroll handling needed: the theme already closes its own
    // quick-search instance on a breakpoint change (handleBreakpointChange()
    // in assets/theme.js), which flips data-quick-search-active - the
    // MutationObserver above already reacts to that and hides this panel the
    // same way it hides the native results. And since the panel now lives
    // inside the same form the theme itself keeps correctly positioned,
    // there's nothing left for scroll/resize to re-sync here either.
  }

  if (typeof module !== "undefined" && module.exports) {
    // Node (unit tests): expose the pure logic.
    module.exports = {
      HISTORY_KEY: HISTORY_KEY,
      MAX_HISTORY: MAX_HISTORY,
      getHistory: getHistory,
      saveTerm: saveTerm,
      escapeHtml: escapeHtml,
      renderHistoryHTML: renderHistoryHTML,
    };
  } else if (typeof document !== "undefined") {
    // Browser: wire up the panel.
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", init);
    } else {
      init();
    }
  }
})();
